import { AddressType, AgentType, ConversationType, SessionType } from "./db"
import { jsonSafeValueToDate } from "./typing"

export type InboundRoute = {
    route_id: string
    team_id: string
    address: string
    address_type: AddressType
    forwarded_from: string | null
    conversation_type: ConversationType
    agent_type: AgentType
    agent_id: string
    session_type: SessionType | null
    weight: number
    finalize_state_id: string | null
}

export enum ComposerType {
    MEGA = 'MEGA',
    SPLIT_STATE = 'SPLIT_STATE'
}

export enum SpeakerSource {
    ELEVEN_LABS = 'ELEVEN_LABS',
    CARTESIA = 'CARTESIA'
}


export type MegaPromptComposer = {
    composer_id: string
    team_id: string
    composer_name: string
    prompt: string
    inbound_open: string | null
    created_at: Date
    substitution_keys_str: string | null
    transfer_keys_str: string | null
}

export function jsonToMegaPromptComposer(data: Record<string, any>): MegaPromptComposer {
    return {
        composer_id: data.composer_id,
        team_id: data.team_id,
        composer_name: data.composer_name,
        prompt: data.prompt,
        inbound_open: data.inbound_open,
        created_at: jsonSafeValueToDate(data.created_at),
        substitution_keys_str: data.substitution_keys_str,
        transfer_keys_str: data.transfer_keys_str
    }
}

export function jsonToMegaPromptComposers(data: Record<string, any>[]): MegaPromptComposer[] {      
    return data.map(jsonToMegaPromptComposer)
}

export type VoiceBot = {
    agent_id: string
    team_id: string
    agent_name: string
    composer_id: string
    composer_type: ComposerType
    substitution_json: Record<string, string> | null
    transfer_json: Record<string, string> | null
    speaker_source: SpeakerSource | null
    voice_id: string | null
}

export function jsonToVoiceBots(data: Record<string, any>[]): VoiceBot[] {
    return data.map(jsonToVoiceBot)
}

export function jsonToVoiceBot(data: Record<string, any>): VoiceBot {
    return {
        agent_id: data.agent_id,
        team_id: data.team_id,
        agent_name: data.agent_name,
        composer_id: data.composer_id,
        composer_type: data.composer_type,
        substitution_json: data.substitution_json ? JSON.parse(data.substitution_json) : null,
        transfer_json: data.transfer_json ? JSON.parse(data.transfer_json) : null,
        speaker_source: data.speaker_source,
        voice_id: data.voice_id
    }
}   

export type ChatBot = {
    agent_id: string
    team_id: string
    agent_name: string
    composer_id: string
    composer_type: ComposerType
    substitution_json: Record<string, string> | null
}

export function jsonToChatBot(data: Record<string, any>): ChatBot {
    return {
        agent_id: data.agent_id,
        team_id: data.team_id,
        agent_name: data.agent_name,
        composer_id: data.composer_id,
        composer_type: data.composer_type,
        substitution_json: data.substitution_json ? JSON.parse(data.substitution_json) : null,
    }
}

export function jsonToChatBots(data: Record<string, any>[]): ChatBot[] {
    return data.map(jsonToChatBot)
}

export enum GoalType {
    STATEMENT = 'STATEMENT',
    DISCOVERY = 'DISCOVERY',
    TRANSFER = 'TRANSFER',
    HANGUP = 'HANGUP',
    WEBHOOK = 'WEBHOOK'
}

export type SplitGoal = {
    composer_id: string
    order: number
    team_id: string
    goal_type: GoalType
    say?: string
    describe?: string
    clarify?: string
    requirement_rule?: string
    update_json_str?: string
    max_attempts: number
    can_interrupt: boolean
    stop_after: boolean
    discovery_field_names_str?: string
    discovery_is_confirmation?: boolean
    transfer_number?: string
    webhook_id?: string
    webhook_parse_json_str?: string
}

export type SplitComposer = {
    composer_id: string
    team_id: string
    composer_name: string
    state_id: string
    created_at: Date
    instruction?: string
}

export function jsonToSplitGoal(data: Record<string, any>): SplitGoal {
    return {
        composer_id: data.composer_id,
        order: data.order,
        team_id: data.team_id,
        goal_type: data.goal_type,
        say: data.say,
        describe: data.describe,
        clarify: data.clarify,
        requirement_rule: data.requirement_rule,
        update_json_str: data.update_json_str,
        max_attempts: data.max_attempts,
        can_interrupt: data.can_interrupt,
        stop_after: data.stop_after,
        discovery_field_names_str: data.discovery_field_names_str,
        discovery_is_confirmation: data.discovery_is_confirmation,
        transfer_number: data.transfer_number,
        webhook_id: data.webhook_id,
        webhook_parse_json_str: data.webhook_parse_json_str
    }
}

export function jsonToSplitGoals(data: Record<string, any>[]): SplitGoal[] {
    return data.map(jsonToSplitGoal)
}

export function jsonToSplitComposer(data: Record<string, any>): SplitComposer {
    return {
        composer_id: data.composer_id,
        team_id: data.team_id,
        composer_name: data.composer_name,
        state_id: data.state_id,
        created_at: jsonSafeValueToDate(data.created_at),
        instruction: data.instruction
    }
}

export function jsonToSplitComposers(data: Record<string, any>[]): SplitComposer[] {
    return data.map(jsonToSplitComposer)
}

export type SplitComposersResponse = {
    composers: SplitComposer[]
    goals: SplitGoal[]
}